import classNames from 'classnames';

import React, { forwardRef, ForwardRefExoticComponent, HTMLAttributes, RefAttributes } from 'react';

import type { Token as Color } from '../../../../export/brand0/colors';
import { Icon } from '../icon';
import { Image } from '../image';
import { cropText } from '../lib/crop-text';
import { Typography } from '../typography';

import styles from './badge.scss';

type Shape = 'default' | 'new';

type Props = RefAttributes<HTMLDivElement> &
  HTMLAttributes<HTMLDivElement> & {
    text: string;
    shape?: Shape;
    badgeColor?: Color | string;
    contentColor?: Color | string;
    icon?: string;
    image?: string;
    font?: string;
    maxLength?: number;
    className?: string;
    style?: React.CSSProperties;
    dataTestId?: string;
  };

const MAX_LENGTH = 15;

const COMPONENT_ID = 'modulor-badge';

export const Badge: ForwardRefExoticComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      text = '',
      shape = 'default',
      badgeColor = 'background-badge',
      contentColor = 'text-badge',
      font,
      maxLength,
      className,
      icon = '',
      image = '',
      dataTestId = COMPONENT_ID,
      style,
      ...rest
    },
    ref,
  ) => {
    const classes = classNames(styles.container, styles[`container-${shape}`], className);

    const resolvedFont = font || (shape === 'new' ? 'caption-1-regular' : 'caption-2-medium-caps');

    return text !== '' ? (
      <div
        data-id={dataTestId}
        data-component-name={COMPONENT_ID}
        data-testid={dataTestId}
        className={classes}
        ref={ref}
        style={{
          backgroundColor: `var(--${badgeColor})`,
          background: badgeColor,
          ...style,
        }}
        {...rest}>
        {icon && (
          <Icon
            className={styles.icon}
            name={icon}
            size={12}
            style={
              {
                '--icon-fill': `var(--${contentColor})`,
                color: contentColor,
              } as React.CSSProperties
            }
          />
        )}
        {image && !icon && <Image name={image} width="12" height="12" className={styles.icon} />}
        <Typography
          dataTestId={`${dataTestId}-text`}
          tag="p"
          variant={resolvedFont}
          style={
            {
              '--text': `var(--${contentColor})`,
              color: contentColor,
            } as React.CSSProperties
          }
          className={styles.text}>
          {cropText(text, maxLength ? maxLength : MAX_LENGTH)}
        </Typography>
      </div>
    ) : null;
  },
);

Badge.displayName = 'Badge';
